import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Button,
    Card,
    CardBody,
    Tabs,
    Tab,
    Spinner,
    DateRangePicker,
    Autocomplete,
    AutocompleteItem,
} from "@heroui/react";
import { LuPrinter } from "react-icons/lu";
import { parseDate } from "@internationalized/date";
import { useFetchData } from "@/hooks";
import {
    PageHeader,
    ThemedText,
    ErrorState,
    EmptyState,
} from "@/components/ui";
import { formatCurrency, formatDate } from "@/utils";
import { LuReceipt } from "react-icons/lu";

const PAYMENT_METHOD_KEYS = [
    { key: "cash", label: "Cash" },
    { key: "transfer", label: "Transfer" },
    { key: "qris", label: "QRIS" },
    { key: "30_hari", label: "30 Hari" },
    { key: "60_hari", label: "60 Hari" },
    { key: "90_hari", label: "90 Hari" },
    { key: "giro", label: "Giro" },
];

const MASTER_CASH_KEYS = [
    { key: "kas_besar", label: "Kas Besar" },
    { key: "bca", label: "BCA" },
    { key: "kas_kecil", label: "Kas Kecil" },
    { key: "mandiri", label: "Mandiri" },
    { key: "bri", label: "BRI" },
];

const TableReport = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const selectedTab = searchParams.get("tab") || "today";
    const todayStr = new Date().toISOString().split("T")[0];
    const customRange = {
        start: parseDate(searchParams.get("start") || todayStr),
        end: parseDate(searchParams.get("end") || todayStr),
    };
    const customerId = searchParams.get("customer_id") || "";

    const updateParams = (updates) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            Object.entries(updates).forEach(([k, v]) => {
                if (v) next.set(k, v);
                else next.delete(k);
            });
            return next;
        });
    };

    const { data: customers } = useFetchData("select/customer");

    const formatDateParam = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const d = String(date.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
    };

    const getReportParams = () => {
        const today = new Date();
        const base = { customer_id: customerId || undefined };

        if (selectedTab === "today") {
            return {
                ...base,
                start_date: formatDateParam(today),
                end_date: formatDateParam(today),
            };
        }

        if (selectedTab === "yesterday") {
            const yesterday = new Date(today);
            yesterday.setDate(today.getDate() - 1);
            const d = formatDateParam(yesterday);
            return { ...base, start_date: d, end_date: d };
        }

        if (selectedTab === "this_month") {
            const start = new Date(today.getFullYear(), today.getMonth(), 1);
            const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            return {
                ...base,
                start_date: formatDateParam(start),
                end_date: formatDateParam(end),
            };
        }

        if (selectedTab === "custom") {
            return {
                ...base,
                start_date: customRange.start.toString(),
                end_date: customRange.end.toString(),
            };
        }

        return base;
    };

    const { data, isLoading, error, refetch } = useFetchData(
        "report/cash-recap",
        getReportParams(),
    );

    const salesData = data?.sales || [];
    const creditData = data?.credit_payments || [];
    const period = data?.period || {};

    // Totals for sales
    const salesTotals = PAYMENT_METHOD_KEYS.reduce((acc, { key }) => {
        acc[key] = salesData.reduce((sum, row) => sum + (row[key] || 0), 0);
        return acc;
    }, {});
    salesTotals.total = salesData.reduce(
        (sum, row) => sum + (row.total || 0),
        0,
    );

    // Totals for credit
    const creditTotals = MASTER_CASH_KEYS.reduce((acc, { key }) => {
        acc[key] = creditData.reduce((sum, row) => sum + (row[key] || 0), 0);
        return acc;
    }, {});
    creditTotals.total = creditData.reduce(
        (sum, row) => sum + (row.total || 0),
        0,
    );

    const periodDisplay =
        period.start === period.end
            ? formatDate(period.start)
            : `${formatDate(period.start)} - ${formatDate(period.end)}`;

    if (error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Laporan"
                    description="Terjadi kesalahan saat memuat data rekap kas."
                    onAction={refetch}
                />
            </div>
        );
    }

    return (
        <div className="flex h-full w-full flex-col gap-4">
            <div className="flex items-center justify-between">
                <PageHeader
                    title="Rekap Kas"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Laporan" },
                        { label: "Rekap Kas" },
                    ]}
                />
                <Button
                    color="default"
                    size="sm"
                    startContent={<LuPrinter />}
                    isDisabled={
                        isLoading ||
                        (salesData.length === 0 && creditData.length === 0)
                    }
                    onPress={() => {
                        const p = getReportParams();
                        const qs = new URLSearchParams();
                        if (p.start_date) qs.set("start_date", p.start_date);
                        if (p.end_date) qs.set("end_date", p.end_date);
                        if (p.customer_id) qs.set("customer_id", p.customer_id);
                        navigate(`/cash-recap-report/print?${qs.toString()}`);
                    }}
                >
                    Print
                </Button>
            </div>

            {/* Filters */}
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                    <Tabs
                        aria-label="Date Filter"
                        selectedKey={selectedTab}
                        onSelectionChange={(key) => updateParams({ tab: key })}
                        variant="solid"
                        size="sm"
                    >
                        <Tab key="today" title="Hari Ini" />
                        <Tab key="yesterday" title="Kemarin" />
                        <Tab key="this_month" title="Bulan Ini" />
                        <Tab key="custom" title="Custom" />
                    </Tabs>

                    {selectedTab === "custom" && (
                        <DateRangePicker
                            aria-label="Pilih Periode"
                            className="max-w-xs"
                            variant="flat"
                            size="sm"
                            value={customRange}
                            onChange={(range) =>
                                updateParams({
                                    start: range.start.toString(),
                                    end: range.end.toString(),
                                })
                            }
                        />
                    )}
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <Autocomplete
                        aria-label="Filter Customer"
                        placeholder="Semua Customer"
                        variant="flat"
                        size="sm"
                        className="max-w-60"
                        selectedKey={customerId || null}
                        onSelectionChange={(key) =>
                            updateParams({ customer_id: key || "" })
                        }
                        isClearable
                        onClear={() => updateParams({ customer_id: "" })}
                    >
                        {(customers || []).map((item) => (
                            <AutocompleteItem key={item.value}>
                                {item.title}
                            </AutocompleteItem>
                        ))}
                    </Autocomplete>
                </div>
            </div>

            <Card className="border-default-200 border" shadow="none">
                <CardBody className="flex flex-col items-start gap-0.5 px-4 pt-4 pb-0">
                    <ThemedText size="md" className="font-semibold">
                        Rekap Kas
                    </ThemedText>
                    <ThemedText size="xs" variant="caption">
                        Periode: {periodDisplay}
                    </ThemedText>
                </CardBody>
                <CardBody className="overflow-x-auto p-4 pt-2">
                    {isLoading ? (
                        <div className="flex h-48 w-full items-center justify-center">
                            <Spinner label="Memuat data..." color="primary" />
                        </div>
                    ) : salesData.length === 0 && creditData.length === 0 ? (
                        <EmptyState
                            icon={LuReceipt}
                            title="Belum Ada Data"
                            description="Tidak ada transaksi pada periode ini."
                        />
                    ) : (
                        <div className="flex flex-col gap-6">
                            {/* PENJUALAN */}
                            {salesData.length > 0 && (
                                <div>
                                    <ThemedText className="mb-2 text-sm font-semibold">
                                        Penjualan
                                    </ThemedText>
                                    <table className="themed-table">
                                        <thead>
                                            <tr>
                                                <th>No.</th>
                                                <th>Customer</th>
                                                {PAYMENT_METHOD_KEYS.map(
                                                    ({ key, label }) => (
                                                        <th key={key}>
                                                            {label}
                                                        </th>
                                                    ),
                                                )}
                                                <th>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {salesData.map((row, idx) => (
                                                <tr
                                                    key={row.customer_id}
                                                    className="hover:bg-default-50 transition-colors"
                                                >
                                                    <td className="text-center">
                                                        {idx + 1}.
                                                    </td>
                                                    <td>
                                                        {row.customer_name}
                                                    </td>
                                                    {PAYMENT_METHOD_KEYS.map(
                                                        ({ key }) => (
                                                            <td
                                                                key={key}
                                                                className="text-right"
                                                            >
                                                                {row[key]
                                                                    ? formatCurrency(
                                                                          row[key],
                                                                      )
                                                                    : "-"}
                                                            </td>
                                                        ),
                                                    )}
                                                    <td className="text-right font-semibold">
                                                        {formatCurrency(
                                                            row.total,
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td
                                                    colSpan={2}
                                                    className="text-center"
                                                >
                                                    TOTAL
                                                </td>
                                                {PAYMENT_METHOD_KEYS.map(
                                                    ({ key }) => (
                                                        <td
                                                            key={key}
                                                            className="text-right"
                                                        >
                                                            {salesTotals[key]
                                                                ? formatCurrency(
                                                                      salesTotals[key],
                                                                  )
                                                                : "-"}
                                                        </td>
                                                    ),
                                                )}
                                                <td className="text-right">
                                                    {formatCurrency(
                                                        salesTotals.total,
                                                    )}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            )}

                            {/* PEMBAYARAN KREDIT */}
                            {creditData.length > 0 && (
                                <div>
                                    <ThemedText className="mb-2 text-sm font-semibold">
                                        Pembayaran Kredit
                                    </ThemedText>
                                    <table className="themed-table">
                                        <thead>
                                            <tr>
                                                <th>No.</th>
                                                <th>Customer</th>
                                                {MASTER_CASH_KEYS.map(
                                                    ({ key, label }) => (
                                                        <th key={key}>
                                                            {label}
                                                        </th>
                                                    ),
                                                )}
                                                <th>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {creditData.map((row, idx) => (
                                                <tr
                                                    key={row.customer_id}
                                                    className="hover:bg-default-50 transition-colors"
                                                >
                                                    <td className="text-center">
                                                        {idx + 1}.
                                                    </td>
                                                    <td>
                                                        {row.customer_name}
                                                    </td>
                                                    {MASTER_CASH_KEYS.map(
                                                        ({ key }) => (
                                                            <td
                                                                key={key}
                                                                className="text-right"
                                                            >
                                                                {row[key]
                                                                    ? formatCurrency(
                                                                          row[key],
                                                                      )
                                                                    : "-"}
                                                            </td>
                                                        ),
                                                    )}
                                                    <td className="text-right font-semibold">
                                                        {formatCurrency(
                                                            row.total,
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td
                                                    colSpan={2}
                                                    className="text-center"
                                                >
                                                    TOTAL
                                                </td>
                                                {MASTER_CASH_KEYS.map(
                                                    ({ key }) => (
                                                        <td
                                                            key={key}
                                                            className="text-right"
                                                        >
                                                            {creditTotals[key]
                                                                ? formatCurrency(
                                                                      creditTotals[key],
                                                                  )
                                                                : "-"}
                                                        </td>
                                                    ),
                                                )}
                                                <td className="text-right">
                                                    {formatCurrency(
                                                        creditTotals.total,
                                                    )}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

export default TableReport;
